import { useEffect, useState, useCallback } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

export interface ActivationDevice {
    id: number;
    activationId: number;
    deviceKeyId: string;
    friendlyName: string | null;
    lastUploadAt: string | null;
    createdAt: string;
    deviceName: string | null;
    displayName: string;
}

export default function useActivationDevices(activationId: number) {
    const authenticatedApi = useAuthenticatedApi();
    const [devices, setDevices] = useState<ActivationDevice[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState<string | null>(null);

    const fetchDevices = useCallback(async () => {
        if (!activationId) return;
        
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.get(
                `/activations/${activationId}/devices`,
            );
            setDevices(response.data);
        } catch (err) {
            console.error('Error fetching activation devices:', err);
            setError('Failed to fetch devices');
        } finally {
            setIsLoading(false);
        }
    }, [activationId, authenticatedApi]);

    useEffect(() => {
        fetchDevices();

        // Refresh devices every 30 seconds
        const timer = setInterval(() => {
            fetchDevices();
        }, 30000);

        return () => clearInterval(timer);
    }, [fetchDevices]);

    const updateFriendlyName = async (
        deviceKeyId: string,
        friendlyName: string,
    ): Promise<ActivationDevice | null> => {
        setIsUpdating(deviceKeyId);
        setError(null);
        try {
            const response = await authenticatedApi.put(
                `/activations/${activationId}/devices/${deviceKeyId}`,
                { friendlyName },
            );
            
            // Update the local state with the new device data
            setDevices((prev) =>
                prev.map((device) =>
                    device.deviceKeyId === deviceKeyId ? response.data : device,
                ),
            );
            
            return response.data;
        } catch (err) {
            console.error('Error updating device friendly name:', err);
            setError('Failed to update device name');
            return null;
        } finally {
            setIsUpdating(null);
        }
    };

    return {
        devices,
        isLoading,
        error,
        isUpdating,
        updateFriendlyName,
        refreshDevices: fetchDevices,
    };
}
